import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/redux_store'
import { Button, Card, Typography, Space, Alert, Spin, Tag, Badge } from 'antd'
import {
  ExclamationCircleOutlined,
  ReloadOutlined,
  DisconnectOutlined,
  WifiOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import { useParams } from 'react-router-dom'
import { axiosInstance } from '../../interceptor/axios_interceptor'
import { messaging } from '../../Utility/firebase/firebase-test'
import { getToken, onMessage } from 'firebase/messaging'

// Import location data directly
import { srcLatLng2 } from '../../MetaData/srcLatLng2'

// ── Route points: every 10th location from srcLatLng (module-level constant,
//    never re-created on render — critical for stable closures in schedulePoll)
const routePoints = srcLatLng2.filter((_, i) => i % 7 === 0)

const { Title, Text } = Typography

// Hard-coded vendor user ID (replace with redux value when wired up)
const VENDOR_USER_ID = 'FTUSER25060100001'

// ⚠️  DEV-ONLY: hardcoded test token — replace with cookie/redux in production
const DEV_ACCESS_TOKEN =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiJGVFVTRVIyNTA2MDEwMDAwMSIsIm1vYmlsZU51bWJlciI6Ijk3NDI3NTEwMjMiLCJ1c2VyVHlwZSI6IlVTRVIiLCJpYXQiOjE3NzE3NTcwMDcsImV4cCI6MTc3MTc1NzkwN30._g6XRkxIlao9kmR-okDOEiR7n4zmALdcRyfVXyhnghE'

// Fallback polling interval if server doesn't specify (10 seconds)
const DEFAULT_POLL_INTERVAL_MS = 10_000

// ── TrackStatus enum (mirrors backend) ────────────────────────────────────────
type TrackStatus =
  | 'INITIATED_TRACK'
  | 'TRACK_REQUEST'
  | 'TRACK_ONGOING'
  | 'TRACK_STOPPED'
  | 'TRACK_RESTART'
  | 'STATIONARY'

// ── Polling phase ─────────────────────────────────────────────────────────────
type PollingPhase =
  | 'idle'
  | 'starting'
  | 'active'
  | 'calculating'
  | 'stopped'
  | 'error'

// ── Server status response shape ───────────────────────────────────────────────
interface StatusResponse {
  status: string // 'success' | 'calculating' | 'Completed' | 'not_found'
  message?: string
  data?: {
    src?: { lat: number; lng: number }
    dest?: { lat: number; lng: number }
    distanceTimeToReach?: { distance: string; expectedReachTime: number }
    orderStatus?: string
    currentUpdates?: string
  }
  trackStatus?: TrackStatus
  pollingInterval?: number
  nextPollRecommendedAt?: number
  lastUpdated?: number
  dataAge?: number
  trackUpgrade?: boolean
  trackNear?: boolean
  trackReached?: boolean
}

// ── Events response shape ──────────────────────────────────────────────────────
interface TrackEvent {
  timestamp: number
  type: string
  payload: any
}

const TrackPage = () => {
  const params = useParams()
  const reduxUser = useSelector((state: RootState) => state.user)
  const orderId = params.orderId

  // ── State ────────────────────────────────────────────────────────────────────
  const [orderAccepted, setOrderAccepted] = useState(false)
  const [pollingPhase, setPollingPhase] = useState<PollingPhase>('idle')
  const [isTracking, setIsTracking] = useState(false)
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0)

  // Tracking state machine
  const [trackStatus, setTrackStatus] = useState<TrackStatus | undefined>(
    undefined
  )
  const [previousOrderStatus, setPreviousOrderStatus] = useState<
    string | undefined
  >(undefined)
  const [lastPollInterval, setLastPollInterval] = useState<number>(
    DEFAULT_POLL_INTERVAL_MS
  )
  const [lastEventTimestamp, setLastEventTimestamp] = useState<number>(0)

  // Display data
  const [latestStatusData, setLatestStatusData] =
    useState<StatusResponse | null>(null)
  const [serverResponses, setServerResponses] = useState<
    Array<{
      timestamp: number
      data: StatusResponse
      location: { lat: number; lng: number }
    }>
  >([])
  const [trackEvents, setTrackEvents] = useState<TrackEvent[]>([])
  const [notifications, setNotifications] = useState<string[]>([])
  const [fcmToken, setFcmToken] = useState<string | null>(null)

  // ── FCM Integration ──────────────────────────────────────────────────────────
  const updateFcm = async () => {
    try {
      const permission = await Notification.requestPermission()
      console.log('Notification permission:', permission)
      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js'
        )
        // Wait until service worker becomes active
        await navigator.serviceWorker.ready
        const token = await getToken(messaging, {
          vapidKey:
            'BCRaEO0CJKVAYi3LhsBCpakbX8qxIYczs04ujMiKVG2PcSPlQYF-EX2g3oc8607ozzulv4dEiSfNI2Mk4Vsj5H8',
          serviceWorkerRegistration: registration
        })
        if (token) {
          console.log('FCM Token:', token)
          setFcmToken(token)
          // Register token with backend
          await axiosInstance.post('/api/user/updateFcmToken', {
            fcmToken: token,
            deviceType: 'WEB'
          })
          console.log('FCM Token registered with backend')
        } else {
          console.log(
            'No registration token available. Request permission to generate one.'
          )
        }
      } else {
        console.log('Unable to get permission to notify.')
      }
    } catch (err) {
      console.error('An error occurred while retrieving token. ', err)
    }
  }

  useEffect(() => {
    // Handle foreground messages
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload)
      if (payload.notification?.body) {
        setNotifications((n) =>
          [payload.notification!.body!, ...n].slice(0, 10)
        )
      }
    })

    return () => unsubscribe()
  }, [])

  // ── Refs (for use inside setTimeout/setInterval closures) ────────────────────
  const locationIndexRef = useRef(0)
  const trackStatusRef = useRef<TrackStatus | undefined>(undefined)
  const previousOrderStatusRef = useRef<string | undefined>(undefined)
  const lastEventTimestampRef = useRef<number>(0)
  const isTrackingRef = useRef(false)
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Keep refs in sync
  useEffect(() => {
    locationIndexRef.current = currentLocationIndex
  }, [currentLocationIndex])
  useEffect(() => {
    trackStatusRef.current = trackStatus
  }, [trackStatus])
  useEffect(() => {
    previousOrderStatusRef.current = previousOrderStatus
  }, [previousOrderStatus])
  useEffect(() => {
    lastEventTimestampRef.current = lastEventTimestamp
  }, [lastEventTimestamp])

  // ── Inject dev token into cookie + axios Authorization header ────────────────
  useEffect(() => {
    // Set as a browser cookie so withCredentials requests carry it automatically
    document.cookie = `accessToken=${DEV_ACCESS_TOKEN}; path=/; SameSite=Lax`
    console.log('[TrackPage] DEV token injected into cookie')

    // Also set as Bearer header on the shared axiosInstance
    axiosInstance.defaults.headers.common['Authorization'] =
      `Bearer ${DEV_ACCESS_TOKEN}`
    console.log(
      '[TrackPage] DEV token injected into axios Authorization header'
    )

    return () => {
      // Clean up: remove the Authorization header when leaving the page
      delete axiosInstance.defaults.headers.common['Authorization']
      // Expire the cookie
      document.cookie =
        'accessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
      console.log('[TrackPage] DEV token cleaned up')
    }
  }, [])

  // ── Order acceptance check ────────────────────────────────────────────────────
  useEffect(() => {
    if (!orderId) return

    const checkAcceptance = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/orders/otg/orderAccepted/${orderId}`
        )
        if (response.data.responseCode === 0) {
          setOrderAccepted(true)
        }
      } catch (err) {
        console.error('Error checking order acceptance:', err)
      }
    }

    checkAcceptance()
  }, [orderId])

  // // ── Events polling helper ─────────────────────────────────────────────────────
  // const pollEvents = useCallback(async () => {
  //   if (!orderId) return
  //   try {
  //     const res = await axiosInstance.get(`/api/tracking/${orderId}/events`, {
  //       params: {
  //         userId: VENDOR_USER_ID,
  //         since: lastEventTimestampRef.current
  //       }
  //     })
  //     const eventsData = res.data
  //     if (eventsData?.count > 0 && Array.isArray(eventsData.events)) {
  //       setTrackEvents((prev) => [...eventsData.events, ...prev].slice(0, 50))
  //       const maxTs = Math.max(
  //         ...eventsData.events.map((e: TrackEvent) => e.timestamp)
  //       )
  //       setLastEventTimestamp(maxTs)
  //       lastEventTimestampRef.current = maxTs
  //     }
  //   } catch (err) {
  //     console.error('Events poll error:', err)
  //   }
  // }, [orderId])

  // ── Stop polling ──────────────────────────────────────────────────────────────
  const stopTracking = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
    isTrackingRef.current = false
    setIsTracking(false)
    setPollingPhase('stopped')
    console.log('Tracking stopped')
  }, [])

  // ── Core polling tick (recursive setTimeout, interval from server) ────────────
  const schedulePoll = useCallback(
    (intervalMs: number) => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)

      pollTimeoutRef.current = setTimeout(async () => {
        if (!isTrackingRef.current || !orderId) return

        const idx = locationIndexRef.current
        if (idx >= routePoints.length) {
          console.log('All route points exhausted')
          stopTracking()
          return
        }

        const location = routePoints[idx]
        console.log(
          `Polling status [${idx + 1}/${routePoints.length}]`,
          location
        )

        try {
          const res = await axiosInstance.post<StatusResponse>(
            `/api/tracking/onGoing`,
            {
              orderId,
              userId: VENDOR_USER_ID,
              lat: location.lat,
              lng: location.lng,
              ...(trackStatusRef.current && {
                trackStatus: trackStatusRef.current
              }),
              ...(previousOrderStatusRef.current && {
                previousOrderStatus: previousOrderStatusRef.current
              })
            }
          )

          const statusData = res.data
          console.log('Poll response:', statusData)

          // ── Update state machine ──────────────────────────────────────────────
          if (statusData.trackStatus) {
            setTrackStatus(statusData.trackStatus)
          }

          // ── Handle terminal state ─────────────────────────────────────────────
          if (
            statusData.trackStatus === 'TRACK_STOPPED' ||
            statusData.status === 'Completed'
          ) {
            console.log('Tracking ended by server')
            setLatestStatusData(statusData)
            setPollingPhase('stopped')
            stopTracking()
            return
          }

          // ── Handle TRACK_RESTART ──────────────────────────────────────────────
          if (statusData.trackStatus === 'TRACK_RESTART') {
            console.log('Server requested TRACK_RESTART - re-triggering /start')
            setLatestStatusData(statusData)
            // Re-trigger startTracking. Since we are already "tracking", we
            // set the flag to false momentarily to bypass the guard in startTracking.
            isTrackingRef.current = false
            startTracking()
            return
          }

          // ── Handle calculating / not_found ────────────────────────────────────
          if (
            statusData.status === 'calculating' ||
            statusData.status === 'not_found'
          ) {
            setPollingPhase('calculating')
          } else if (
            statusData.status === 'success' ||
            statusData.status === 'Stationary'
          ) {
            setPollingPhase('active')

            // Track detection flags as notifications
            if (statusData.trackUpgrade) {
              setNotifications((n) =>
                [`Order status upgraded!`, ...n].slice(0, 10)
              )
            }
            if (statusData.trackNear) {
              setNotifications((n) =>
                [`🚀 Vehicle is near (< 5 min away)!`, ...n].slice(0, 10)
              )
            }
            if (statusData.trackReached) {
              setNotifications((n) =>
                [`📍 Vehicle has reached!`, ...n].slice(0, 10)
              )
            }

            // Update previousOrderStatus from latest data
            if (statusData.data?.orderStatus) {
              setPreviousOrderStatus(statusData.data.orderStatus)
              previousOrderStatusRef.current = statusData.data.orderStatus
            }
          }

          // ── Store response for display ─────────────────────────────────────────
          setLatestStatusData(statusData)
          setServerResponses((prev) => [
            { timestamp: Date.now(), data: statusData, location },
            ...prev
          ])

          // ── Poll events in parallel ────────────────────────────────────────────
          // pollEvents()

          // ── Advance route point ────────────────────────────────────────────────
          setCurrentLocationIndex((prev) => prev + 1)
          locationIndexRef.current = idx + 1

          // ── Schedule next poll using server-recommended interval ───────────────
          const nextInterval =
            statusData.pollingInterval ?? DEFAULT_POLL_INTERVAL_MS
          setLastPollInterval(nextInterval)
          schedulePoll(nextInterval)
        } catch (err: any) {
          console.error('Status poll error:', err)
          setPollingPhase('error')
          // Retry after default interval even on error
          schedulePoll(DEFAULT_POLL_INTERVAL_MS)
        }
      }, intervalMs)
    },
    [orderId, stopTracking]
  )

  // ── Start tracking: POST /start → then begin polling ─────────────────────────
  const startTracking = useCallback(async () => {
    if (isTrackingRef.current || !orderAccepted || !orderId) return

    const idx = locationIndexRef.current
    if (idx >= routePoints.length) {
      console.log('All route points already sent')
      return
    }

    setPollingPhase('starting')
    const firstLocation = routePoints[idx]

    try {
      const startRes = await axiosInstance.post<StatusResponse>(
        `/api/tracking/start`,
        {
          orderId,
          userId: VENDOR_USER_ID,
          lat: firstLocation.lat,
          lng: firstLocation.lng
        }
      )

      const startData = startRes.data
      console.log('Start response:', startData)

      // Capture initial trackStatus from start response
      if (startData.trackStatus) {
        setTrackStatus(startData.trackStatus)
        trackStatusRef.current = startData.trackStatus
      }

      // Advance past the first point (we just sent it in /start)
      setCurrentLocationIndex(idx + 1)
      locationIndexRef.current = idx + 1

      isTrackingRef.current = true
      setIsTracking(true)
      setPollingPhase('active')

      // Use server-recommended interval or default
      const firstInterval =
        startData.pollingInterval ?? DEFAULT_POLL_INTERVAL_MS
      setLastPollInterval(firstInterval)
      schedulePoll(firstInterval)
    } catch (err: any) {
      console.error('Failed to start tracking:', err)
      setPollingPhase('error')
    }
  }, [orderAccepted, orderId, routePoints, schedulePoll])

  // ── Reset route ───────────────────────────────────────────────────────────────
  const resetRoute = useCallback(() => {
    stopTracking()
    setCurrentLocationIndex(0)
    locationIndexRef.current = 0
    setServerResponses([])
    setTrackEvents([])
    setLatestStatusData(null)
    setTrackStatus(undefined)
    setPreviousOrderStatus(undefined)
    setLastEventTimestamp(0)
    lastEventTimestampRef.current = 0
    setNotifications([])
    setPollingPhase('idle')
  }, [stopTracking])

  // ── Cleanup on unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
  }, [])

  // ── UI helpers ────────────────────────────────────────────────────────────────
  // textType must be a valid Ant Design BaseType: 'secondary'|'success'|'warning'|'danger'|undefined
  type BaseType = 'secondary' | 'success' | 'warning' | 'danger' | undefined

  const getPollingStatusDisplay = (): {
    textType: BaseType
    text: string
    icon: React.ReactNode
  } => {
    switch (pollingPhase) {
      case 'active':
        return {
          textType: 'success',
          text: `Polling Active (${lastPollInterval / 1000}s interval)`,
          icon: <WifiOutlined />
        }
      case 'calculating':
        return {
          textType: 'warning',
          text: 'Calculating (poll again in 10s)',
          icon: <Spin size='small' />
        }
      case 'starting':
        return {
          textType: undefined,
          text: 'Starting...',
          icon: <Spin size='small' />
        }
      case 'error':
        return {
          textType: 'danger',
          text: 'Poll Error (retrying...)',
          icon: <ExclamationCircleOutlined />
        }
      case 'stopped':
        return {
          textType: 'secondary',
          text: 'Stopped',
          icon: <DisconnectOutlined />
        }
      default:
        return {
          textType: 'secondary',
          text: 'Idle',
          icon: <DisconnectOutlined />
        }
    }
  }

  const statusDisplay = getPollingStatusDisplay()
  const progressPercentage =
    routePoints.length > 0
      ? (currentLocationIndex / routePoints.length) * 100
      : 0

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <Title level={2}>Order Tracking System</Title>

      {/* Notification strip */}
      {notifications.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          {notifications.slice(0, 3).map((n, i) => (
            <Alert
              key={i}
              message={n}
              type='info'
              showIcon
              closable
              style={{ marginBottom: '4px' }}
            />
          ))}
        </div>
      )}

      {/* Status Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '16px'
        }}
      >
        {/* Order Status */}
        <Card>
          <Space direction='vertical' style={{ width: '100%' }}>
            <Text strong>Order Status</Text>
            <Text type={orderAccepted ? 'success' : 'warning'}>
              {orderAccepted ? '✅ Order Accepted' : '⏳ Checking Order...'}
            </Text>
            {orderId && <Text type='secondary'>Order ID: {orderId}</Text>}
          </Space>
        </Card>

        {/* Polling Status */}
        <Card>
          <Space direction='vertical' style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Text strong>Polling Status</Text>
              {(pollingPhase === 'stopped' || pollingPhase === 'error') && (
                <Button
                  type='primary'
                  icon={<ReloadOutlined />}
                  onClick={resetRoute}
                  size='small'
                >
                  Restart
                </Button>
              )}
            </div>
            <Space>
              {statusDisplay.icon}
              <Text type={statusDisplay.textType}>{statusDisplay.text}</Text>
            </Space>
            {trackStatus && (
              <Tag
                color={
                  trackStatus === 'TRACK_ONGOING'
                    ? 'green'
                    : trackStatus === 'TRACK_STOPPED'
                      ? 'red'
                      : trackStatus === 'TRACK_REQUEST'
                        ? 'orange'
                        : trackStatus === 'TRACK_RESTART'
                          ? 'volcano'
                          : trackStatus === 'STATIONARY'
                            ? 'gold'
                            : 'blue'
                }
              >
                {trackStatus}
              </Tag>
            )}
          </Space>
        </Card>
      </div>

      {/* Latest tracking data */}
      {latestStatusData?.data && (
        <Card title='Live Tracking Data' style={{ marginBottom: '16px' }}>
          <Space direction='vertical' style={{ width: '100%' }}>
            {latestStatusData.data.orderStatus && (
              <Text>
                <strong>Order Status:</strong>{' '}
                <Tag>{latestStatusData.data.orderStatus}</Tag>
              </Text>
            )}
            {latestStatusData.data.src && (
              <Text>
                <EnvironmentOutlined /> <strong>Source:</strong>{' '}
                {latestStatusData.data.src.lat.toFixed(6)},{' '}
                {latestStatusData.data.src.lng.toFixed(6)}
              </Text>
            )}
            {latestStatusData.data.dest && (
              <Text>
                <EnvironmentOutlined /> <strong>Destination:</strong>{' '}
                {latestStatusData.data.dest.lat.toFixed(6)},{' '}
                {latestStatusData.data.dest.lng.toFixed(6)}
              </Text>
            )}
            {latestStatusData.data.distanceTimeToReach && (
              <Text>
                <ClockCircleOutlined /> <strong>ETA:</strong>{' '}
                {latestStatusData.data.distanceTimeToReach.distance} —{' '}
                {new Date(
                  latestStatusData.data.distanceTimeToReach.expectedReachTime
                ).toLocaleTimeString()}
              </Text>
            )}
            <Space>
              {latestStatusData.trackUpgrade && (
                <Tag color='purple'>⬆ Status Upgraded</Tag>
              )}
              {latestStatusData.trackNear && <Tag color='orange'>🚀 Near</Tag>}
              {latestStatusData.trackReached && (
                <Tag color='green'>📍 Reached</Tag>
              )}
            </Space>
          </Space>
        </Card>
      )}

      {/* Route Progress */}
      {orderAccepted && (
        <Card title='Route Progress' style={{ marginBottom: '16px' }}>
          <Space direction='vertical' style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Text>
                Location: {currentLocationIndex} / {routePoints.length} (
                {progressPercentage.toFixed(1)}%)
              </Text>
              <Space>
                <Button
                  type='primary'
                  icon={<PlayCircleOutlined />}
                  onClick={updateFcm}
                >
                  Update FCM
                </Button>
                {!isTracking ? (
                  <Button
                    type='primary'
                    icon={<PlayCircleOutlined />}
                    onClick={startTracking}
                    disabled={
                      currentLocationIndex >= routePoints.length ||
                      pollingPhase === 'stopped'
                    }
                  >
                    Start Tracking
                  </Button>
                ) : (
                  <Button
                    type='default'
                    icon={<PauseCircleOutlined />}
                    onClick={stopTracking}
                  >
                    Stop Tracking
                  </Button>
                )}
                <Button onClick={resetRoute}>Reset Route</Button>
              </Space>
            </div>

            <div
              style={{
                width: '100%',
                height: '10px',
                backgroundColor: '#f0f0f0',
                borderRadius: '5px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  width: `${progressPercentage}%`,
                  height: '100%',
                  backgroundColor: '#52c41a',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>

            {isTracking && (
              <Alert
                message={`Tracking active — next poll in ~${lastPollInterval / 1000}s`}
                type='info'
                showIcon
              />
            )}

            {currentLocationIndex > 0 &&
              currentLocationIndex < routePoints.length && (
                <Text type='secondary'>
                  Sending: {routePoints[currentLocationIndex]?.lat.toFixed(6)},{' '}
                  {routePoints[currentLocationIndex]?.lng.toFixed(6)}
                </Text>
              )}
          </Space>
        </Card>
      )}

      {/* Track Events (from Kafka via Redis) */}
      {trackEvents.length > 0 && (
        <Card
          title={
            <span>
              Track Events{' '}
              <Badge
                count={trackEvents.length}
                style={{ backgroundColor: '#fa8c16' }}
              />
            </span>
          }
          style={{ marginBottom: '16px' }}
        >
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            <Space direction='vertical' style={{ width: '100%' }}>
              {trackEvents.slice(0, 3).map((event, index) => (
                <Card
                  key={index}
                  size='small'
                  style={{ background: '#fff7e6', border: '1px solid #ffd591' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '4px'
                    }}
                  >
                    <Tag color='orange'>{event.type}</Tag>
                    <Text type='secondary'>
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </Text>
                  </div>
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: '6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      margin: 0
                    }}
                  >
                    {JSON.stringify(event.payload, null, 2)}
                  </pre>
                </Card>
              ))}
            </Space>
          </div>
        </Card>
      )}

      {/* Server Responses */}
      {serverResponses.length > 0 && (
        <Card title='Poll History' style={{ marginBottom: '16px' }}>
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <Space direction='vertical' style={{ width: '100%' }}>
              {serverResponses.map((response, index) => (
                <Card
                  key={index}
                  size='small'
                  style={{
                    background: index === 0 ? '#f6ffed' : '#fafafa',
                    border:
                      index === 0 ? '1px solid #b7eb8f' : '1px solid #d9d9d9'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}
                  >
                    <Space>
                      <Text strong>Poll #{serverResponses.length - index}</Text>
                      {response.data.trackStatus && (
                        <Tag>{response.data.trackStatus}</Tag>
                      )}
                    </Space>
                    <Text type='secondary'>
                      {new Date(response.timestamp).toLocaleTimeString()}
                    </Text>
                  </div>
                  <Text type='secondary' style={{ fontSize: '12px' }}>
                    Sent: {response.location.lat.toFixed(6)},{' '}
                    {response.location.lng.toFixed(6)}
                  </Text>
                  {response.data.pollingInterval !== undefined && (
                    <Text
                      type='secondary'
                      style={{ fontSize: '12px', display: 'block' }}
                    >
                      Next poll in: {response.data.pollingInterval / 1000}s
                    </Text>
                  )}
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: '8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      margin: '8px 0 0 0',
                      overflow: 'auto'
                    }}
                  >
                    {JSON.stringify(response.data, null, 2)}
                  </pre>
                </Card>
              ))}
            </Space>
          </div>
        </Card>
      )}

      {!orderAccepted && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type='secondary'>
              Waiting for order to be accepted before starting tracking...
            </Text>
          </div>
        </Card>
      )}
    </div>
  )
}

export default TrackPage
